import { useSyncExternalStore } from 'react';
import { DEFAULT_P2P_DEMO_SCENARIO_ID, getP2PDemoScenario } from './vcDemoScenarios';
import type {
  P2PDemoAuditEvent,
  P2PDemoEvent,
  P2PDemoScenarioId,
  P2PDemoSession,
} from './vcDemoTypes';

const STORAGE_KEY = 'link.p2pGenieVcDemo2.v1';
const FIXED_SIMULATION_START = '2026-07-13T18:00:00.000Z';
const listeners = new Set<() => void>();
let cachedRaw: string | null | undefined;
let cachedSession: P2PDemoSession;

export const DEFAULT_P2P_DEMO_SESSION: P2PDemoSession = {
  version: 1,
  scenarioId: DEFAULT_P2P_DEMO_SCENARIO_ID,
  stage: 'off',
  simulatedAt: null,
  submittedAt: null,
  denialReceivedAt: null,
  outcome: null,
  codingCorrectionApplied: false,
  auditEvents: [],
};

function canUseStorage(): boolean {
  return typeof window !== 'undefined' && typeof window.localStorage !== 'undefined';
}

function normalizeSession(value: Partial<P2PDemoSession> | null | undefined): P2PDemoSession {
  if (!value || value.version !== 1) return DEFAULT_P2P_DEMO_SESSION;
  return {
    ...DEFAULT_P2P_DEMO_SESSION,
    ...value,
    scenarioId: value.scenarioId || DEFAULT_P2P_DEMO_SCENARIO_ID,
    auditEvents: Array.isArray(value.auditEvents) ? value.auditEvents : [],
  };
}

export function getP2PDemoSession(): P2PDemoSession {
  if (!canUseStorage()) return DEFAULT_P2P_DEMO_SESSION;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw === cachedRaw && cachedSession) return cachedSession;
    cachedRaw = raw;
    cachedSession = raw
      ? normalizeSession(JSON.parse(raw) as Partial<P2PDemoSession>)
      : DEFAULT_P2P_DEMO_SESSION;
    return cachedSession;
  } catch {
    return DEFAULT_P2P_DEMO_SESSION;
  }
}

function writeSession(session: P2PDemoSession): P2PDemoSession {
  const serialized = JSON.stringify(session);
  cachedRaw = serialized;
  cachedSession = session;
  if (canUseStorage()) {
    try {
      window.localStorage.setItem(STORAGE_KEY, serialized);
    } catch {
      // Demo persistence must never interrupt the clinical workflow.
    }
  }
  listeners.forEach((listener) => listener());
  return session;
}

function timestampForEvent(session: P2PDemoSession, event: P2PDemoEvent): string {
  if ('at' in event && event.at) return event.at;

  if (event.type === 'select_scenario' || event.type === 'arm') {
    return FIXED_SIMULATION_START;
  }

  if (event.type === 'authorization_submitted') {
    return session.simulatedAt || FIXED_SIMULATION_START;
  }

  if (event.type === 'payer_denial_received') {
    const submittedAt = session.submittedAt || session.simulatedAt || FIXED_SIMULATION_START;
    return new Date(Date.parse(submittedAt) + 48 * 60 * 60 * 1000).toISOString();
  }

  return session.denialReceivedAt || session.submittedAt || session.simulatedAt || FIXED_SIMULATION_START;
}

function auditEvent(
  type: P2PDemoAuditEvent['type'],
  label: string,
  at: string,
  index: number,
): P2PDemoAuditEvent {
  return {
    id: `${type}-${at}-${index}`,
    type,
    at,
    label,
  };
}

function appendAudit(
  session: P2PDemoSession,
  type: P2PDemoAuditEvent['type'],
  label: string,
  at: string,
): P2PDemoAuditEvent[] {
  return [...session.auditEvents, auditEvent(type, label, at, session.auditEvents.length)];
}

export function transitionP2PDemoSession(
  session: P2PDemoSession,
  event: P2PDemoEvent,
): P2PDemoSession {
  if (event.type === 'reset') return DEFAULT_P2P_DEMO_SESSION;

  const at = timestampForEvent(session, event);
  const scenario = getP2PDemoScenario(
    event.type === 'select_scenario' || event.type === 'arm'
      ? event.scenarioId || session.scenarioId
      : session.scenarioId,
  );

  switch (event.type) {
    case 'select_scenario':
      if (session.stage !== 'off') return session;
      return {
        ...DEFAULT_P2P_DEMO_SESSION,
        scenarioId: event.scenarioId,
        auditEvents: [auditEvent('scenario_selected', `${scenario.patientName} scenario selected`, at, 0)],
      };
    case 'arm':
      if (session.stage !== 'off') return session;
      return {
        ...session,
        scenarioId: scenario.id,
        stage: 'armed',
        simulatedAt: at,
        auditEvents: appendAudit(session, 'simulation_armed', `Denial simulation armed for ${scenario.patientName}`, at),
      };
    case 'authorization_submitted':
      if (session.stage !== 'armed' || event.caseId !== scenario.caseId) return session;
      return {
        ...session,
        stage: 'payer_pending',
        submittedAt: at,
        auditEvents: appendAudit(session, 'authorization_submitted', 'Submitted authorization snapshot frozen for payer review', at),
      };
    case 'payer_denial_received':
      if (session.stage !== 'payer_pending') return session;
      return {
        ...session,
        stage: 'denied',
        denialReceivedAt: at,
        auditEvents: appendAudit(session, 'payer_denial_received', `${scenario.payerName} denial received with P2P available`, at),
      };
    case 'open_p2p':
      if (session.stage !== 'denied' && session.stage !== 'p2p_ready') return session;
      return {
        ...session,
        stage: 'p2p_ready',
        auditEvents: session.stage === 'p2p_ready'
          ? session.auditEvents
          : appendAudit(session, 'p2p_opened', 'P2PGenie call preparation opened', at),
      };
    case 'apply_coding_correction':
      if (scenario.id !== 'thomas-palmer' || session.stage !== 'p2p_ready') return session;
      return {
        ...session,
        codingCorrectionApplied: true,
        auditEvents: appendAudit(session, 'coding_correction_applied', 'Modifier LT aligned with submitted left-shoulder evidence', at),
      };
    case 'record_outcome': {
      if (session.stage !== 'p2p_ready') return session;
      if (event.outcome === 'overturned') {
        return {
          ...session,
          stage: 'overturned',
          outcome: event.outcome,
          auditEvents: appendAudit(session, 'outcome_recorded', 'P2P outcome recorded: authorization overturned', at),
        };
      }

      const blocked = scenario.evidence.some((finding) => finding.status === 'blocked') &&
        !(scenario.id === 'thomas-palmer' && session.codingCorrectionApplied);
      const outcomeLabel = event.outcome === 'failed' ? 'denied' : 'timed out';
      const compilationTrigger = event.outcome === 'failed' ? 'denied P2P' : 'timed-out P2P';
      const withOutcome: P2PDemoSession = {
        ...session,
        stage: blocked ? 'documentation_review_required' : 'appeal_ready',
        outcome: event.outcome,
        auditEvents: appendAudit(session, 'outcome_recorded', `P2P outcome recorded: ${outcomeLabel}`, at),
      };

      return blocked
        ? withOutcome
        : {
            ...withOutcome,
            auditEvents: appendAudit(
              withOutcome,
              'appeal_compiled',
              `Pre-service clinical appeal auto-drafted from frozen submitted evidence after ${compilationTrigger}`,
              at,
            ),
          };
    }
    case 'route_to_ops':
      if (session.stage !== 'appeal_ready') return session;
      return {
        ...session,
        stage: 'routed',
        auditEvents: appendAudit(session, 'routed_to_ops', 'Appeal packet routed to LINK Ops in simulation', at),
      };
  }
}

export function dispatchP2PDemoEvent(event: P2PDemoEvent): P2PDemoSession {
  return writeSession(transitionP2PDemoSession(getP2PDemoSession(), event));
}

export function resetP2PDemoSession(): P2PDemoSession {
  cachedRaw = null;
  cachedSession = DEFAULT_P2P_DEMO_SESSION;
  if (canUseStorage()) {
    try {
      window.localStorage.removeItem(STORAGE_KEY);
    } catch {
      // Reset is best-effort in browser privacy modes.
    }
  }
  listeners.forEach((listener) => listener());
  return DEFAULT_P2P_DEMO_SESSION;
}

export function subscribeP2PDemoSession(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function useP2PDemoSession(): P2PDemoSession {
  return useSyncExternalStore(
    subscribeP2PDemoSession,
    getP2PDemoSession,
    () => DEFAULT_P2P_DEMO_SESSION,
  );
}

export function isP2PGenieVcDemoEnabled(): boolean {
  return import.meta.env.VITE_P2P_GENIE_DEMO !== 'false';
}
